import React, { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import { EmtTypography, EmtButton, EmtIcon } from 'emt-components-v2'
import { useIsResponsive } from '@/hooks'
import { scrollToFirstError } from '@/utils'
import { useSignUpCallRequestStore } from '@/store/signUpCallRequestStore'
import ServiceContentSection from '@/components/pages/sign-up-call-request/ServiceContentForm'

// Types
import { Address } from '@/dataV3/Common/Address'
import { services } from '@/components/pages/pet-sitter/reservations/NewRequest/requestConfig'

interface ServiceStepComponentProps {
  onNext: () => void
  footerContent?: React.ReactNode
  canContinue?: boolean
}

export interface ServiceStepFormValues {
  address: Address
}

const ServiceStep: React.FC<ServiceStepComponentProps> = ({ onNext, footerContent, canContinue = true }) => {
  const [isMobile] = useIsResponsive()
  const serviceSectionRef = useRef<HTMLDivElement | null>(null)
  const setDistance = useSignUpCallRequestStore((s) => s.setDistance)
  const setService = useSignUpCallRequestStore((s) => s.setService)
  const service = useSignUpCallRequestStore((s) => s.service)

  const [loading, setLoading] = useState(false)
  const [selectedService, setSelectedService] = useState<string | null>(service?.selectedService || null)

  // Add state for slots and refs if you want to use ServiceContentSection fully
  const formRefs = useRef<Record<string, any>>({})
  const serviceContentRef = useRef<any>(null)

  const handleSubmit = async () => {
    setLoading(true)
    // Validate ServiceContentSection
    if (serviceContentRef.current && serviceContentRef.current.validate) {
      const validForm = await serviceContentRef.current.validate()
      if (!validForm) {
        setLoading(false)
        scrollToFirstError()
        return
      }
    }
    const values = serviceContentRef.current.getValues()
    setService({ selectedService: values.selectedService, formSlots: values.formSlots })
    setDistance(values.distance)
    setLoading(false)
    onNext()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
      <Box>
        <EmtTypography variant="h3" fontWeight="800" sx={{ fontSize: isMobile ? '24px' : '34px' }}>
          De quel service avez-vous besoin ?
        </EmtTypography>
      </Box>

      {!selectedService && (
        <Box
          sx={{
            display: isMobile ? 'flex' : 'grid',
            gridTemplateColumns: '1fr 1fr',
            flexDirection: isMobile ? 'column' : 'initial',
            gap: '15px',
          }}
        >
          {services.map((service, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: isMobile ? 'row' : 'column',
                gap: '15px',
                backgroundColor: '#fff',
                padding: isMobile ? '16px' : '30px',
                borderRadius: '16px',
                border: '1px solid #E8E8EA',
                cursor: 'pointer',
              }}
              onClick={() => setSelectedService(service.slug)}
            >
              <EmtIcon
                className={service.iconClass}
                sx={{ color: 'purple.main', fontSize: isMobile ? '18px' : '30px' }}
              />
              <Box>
                <EmtTypography variant="body1" fontWeight="700" textAlign={!isMobile ? 'center' : 'left'}>
                  {service.title}
                </EmtTypography>
                <EmtTypography
                  variant="body2"
                  fontSize="14px"
                  color="#7A7880"
                  textAlign={!isMobile ? 'center' : 'left'}
                >
                  {service.priceLabel}
                </EmtTypography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Show ServiceContentSection only when a service is selected */}
      {selectedService && (
        <>
          <ServiceContentSection
            ref={serviceContentRef}
            isMobile={isMobile}
            selectedService={selectedService}
            formRefs={formRefs}
            localStorageInitialValues={service || null}
            onServiceChange={setSelectedService}
            serviceSectionRef={serviceSectionRef}
            showDistanceRange={false}
          />
          {footerContent}
          <EmtButton
            fullWidth
            color="purple"
            size="large"
            variant="contained"
            disabled={loading || !canContinue}
            onClick={handleSubmit}
            loader={loading}
          >
            Continuer
          </EmtButton>
        </>
      )}
    </Box>
  )
}

export default ServiceStep
